"use client";

import * as React from "react";
import { motion } from "framer-motion";
import { twMerge } from "tailwind-merge";
import { Slider } from "@/components/ui/slider";

export type HistogramBucket = {
  min: number;
  max: number | null;
  count: number;
};

type Props = {
  buckets: HistogramBucket[] | null | undefined;
  isLoading?: boolean;
  className?: string;
  barCount: number;
  selectedMin?: number | null;
  selectedMax?: number | null;
  formatLabel?: (b: HistogramBucket) => string;
  onBucketClick?: (b: HistogramBucket) => void;
  /** Called once a slider drag ends; undefined means open-ended. */
  onRangeChange?: (min: number | undefined, max: number | undefined) => void;
  thumbLabels?: [string, string];
  type: "price" | "size";
};

const clamp01 = (n: number) => Math.max(0, Math.min(1, n));

const defaultFormatLabel = (b: HistogramBucket) => `${b.min}–${b.max ?? "∞"}`;

const isOutsideRange = (
  b: HistogramBucket,
  min?: number | null,
  max?: number | null
) => {
  if (min == null && max == null) return false;

  const bMin = b.min;
  const bMax = b.max ?? Number.POSITIVE_INFINITY;

  if (min != null && bMax <= min) return true;
  if (max != null && bMin >= max) return true;
  return false;
};

const normalize = (raw: HistogramBucket[]) => {
  const maxCount = Math.max(1, ...raw.map((b) => b.count || 0));
  return raw.map((b) => ({
    ...b,
    norm: clamp01((b.count || 0) / maxCount),
  }));
};

/**
 * Bars are equal width while buckets are not, so the slider works in bar
 * positions: position i is the left edge of bar i, position n (after the last,
 * open-ended bar) means "no limit". Typed values between edges are placed
 * proportionally inside their bar.
 */
const valueToPosition = (buckets: HistogramBucket[], value: number) => {
  for (let i = 0; i < buckets.length; i++) {
    const { min, max } = buckets[i];
    if (max === null) return value <= min ? i : i + 0.5;
    if (value < max) return i + clamp01((value - min) / (max - min));
  }
  return buckets.length;
};

const FilterHistogram: React.FC<Props> = ({
  buckets,
  isLoading = false,
  className,
  barCount,
  selectedMin,
  selectedMax,
  formatLabel = defaultFormatLabel,
  onBucketClick,
  onRangeChange,
  thumbLabels,
  type,
}) => {
  const raw = React.useMemo(
    () => (Array.isArray(buckets) ? buckets : []),
    [buckets]
  );

  const showSkeleton = raw.length === 0 && isLoading;

  const normalized = React.useMemo(() => normalize(raw), [raw]);

  const committed = React.useMemo<[number, number]>(
    () => [
      selectedMin == null ? 0 : valueToPosition(raw, selectedMin),
      selectedMax == null ? raw.length : valueToPosition(raw, selectedMax),
    ],
    [raw, selectedMin, selectedMax]
  );
  // Local position while dragging, so the search only refetches on release.
  const [dragging, setDragging] = React.useState<[number, number] | null>(
    null
  );
  const position = dragging ?? committed;
  const edgeValue = (index: number) =>
    index >= raw.length ? undefined : raw[index].min;
  const dragMin = dragging ? edgeValue(dragging[0]) : selectedMin;
  const dragMax = dragging ? edgeValue(dragging[1]) : selectedMax;

  return (
    <div className={twMerge("flex w-full flex-col", className)}>
      {/* Radix keeps thumbs inside the track, so inset the bars by half a
          thumb (px-3) to line the thumbs up with the bar edges. */}
      <div
        className={twMerge(
          "flex h-20 w-full items-end gap-1",
          onRangeChange && "px-3"
        )}
      >
        {showSkeleton
          ? Array.from({ length: barCount }).map((_, i) => (
              <div
                key={`hist-skel-${type}-${i}`}
                className="flex-1 rounded-t-md bg-muted/60 animate-pulse"
                style={{ height: `${20 + (i % 5) * 12}%` }}
              />
            ))
          : normalized.map((b, idx) => {
              const heightPct = Math.max(0.04, b.norm) * 100;
              const dimmed = isOutsideRange(b, dragMin, dragMax);
              const label = formatLabel(b);

              return (
                <button
                  key={`hist-bucket-${type}-${idx}`}
                  type="button"
                  onClick={() => onBucketClick?.(b)}
                  className={twMerge(
                    "group relative h-full flex-1 rounded-t-md outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background",
                    onBucketClick ? "cursor-pointer" : "cursor-default"
                  )}
                  aria-label={`${label}: ${b.count}`}
                >
                  <div className="flex h-full w-full items-end">
                    <motion.div
                      className={twMerge(
                        "w-full rounded-t-md transition-colors",
                        dimmed
                          ? "bg-zinc-200"
                          : "bg-primary group-hover:bg-zinc-700"
                      )}
                      initial={false}
                      animate={{
                        height: `${heightPct}%`,
                        opacity: isLoading ? 0.75 : 1,
                      }}
                      transition={{
                        type: "spring",
                        stiffness: 260,
                        damping: 26,
                      }}
                    />
                  </div>

                  <div className="pointer-events-none absolute -top-11 left-1/2 z-10 hidden -translate-x-1/2 whitespace-nowrap rounded-md border bg-background px-2 py-1 text-xs shadow-sm group-hover:block">
                    <div className="font-medium">{label}</div>
                    <div className="text-muted-foreground">{b.count}</div>
                  </div>
                </button>
              );
            })}
      </div>
      {onRangeChange && raw.length > 0 && (
        <Slider
          min={0}
          max={raw.length}
          step={1}
          minStepsBetweenThumbs={1}
          value={position}
          onValueChange={(next) => setDragging([next[0], next[1]])}
          onValueCommit={(next) => {
            setDragging(null);
            onRangeChange(edgeValue(next[0]), edgeValue(next[1]));
          }}
          thumbLabels={thumbLabels}
          className="-mt-px"
          trackClassName="h-1 rounded-none bg-zinc-200"
          rangeClassName="bg-primary"
          thumbClassName="size-6 border-2 border-primary bg-background shadow-md"
        />
      )}
    </div>
  );
};

export default FilterHistogram;
